import * as THREE from 'three';

const MAX_PARTICLES = 400;

/**
 * Small CPU-simulated particle cone from the spray can nozzle while spraying.
 * Purely cosmetic — the paint itself is applied by the PaintSystem.
 */
export class NozzleParticles {
  readonly points: THREE.Points;
  private positions: Float32Array;
  private colors: Float32Array;
  private lives: Float32Array;
  private lifeNorm: Float32Array;
  private velocities: Float32Array;
  private maxLives: Float32Array;
  private cursor = 0;
  private geometry: THREE.BufferGeometry;
  private emitRemainder = 0;

  constructor() {
    this.positions = new Float32Array(MAX_PARTICLES * 3);
    this.colors = new Float32Array(MAX_PARTICLES * 3);
    this.lives = new Float32Array(MAX_PARTICLES);
    this.lifeNorm = new Float32Array(MAX_PARTICLES);
    this.maxLives = new Float32Array(MAX_PARTICLES);
    this.velocities = new Float32Array(MAX_PARTICLES * 3);

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('aColor', new THREE.BufferAttribute(this.colors, 3));
    this.geometry.setAttribute('aLife', new THREE.BufferAttribute(this.lifeNorm, 1));

    const material = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
      vertexShader: /* glsl */ `
        attribute vec3 aColor;
        attribute float aLife;
        varying vec3 vColor;
        varying float vLife;
        void main() {
          vColor = aColor;
          vLife = aLife;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = (aLife > 0.0 ? 26.0 : 0.0) / max(-mv.z, 0.5);
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: /* glsl */ `
        varying vec3 vColor;
        varying float vLife;
        void main() {
          float d = length(gl_PointCoord - 0.5) * 2.0;
          float a = smoothstep(1.0, 0.2, d) * vLife * 0.55;
          if (a < 0.01) discard;
          gl_FragColor = vec4(vColor, a);
        }
      `,
    });

    this.points = new THREE.Points(this.geometry, material);
    this.points.frustumCulled = false;
    this.points.renderOrder = 5;
  }

  /** Emit a cone of droplets from `origin` toward `target`. */
  emit(origin: THREE.Vector3, target: THREE.Vector3, color: THREE.Color, dt: number): void {
    const dir = target.clone().sub(origin);
    const dist = Math.max(dir.length(), 0.001);
    dir.normalize();
    this.emitRemainder += dt * 240;
    const count = Math.floor(this.emitRemainder);
    this.emitRemainder -= count;
    const speed = Math.min(dist * 4.5, 22);
    for (let n = 0; n < count; n++) {
      const i = this.cursor;
      this.cursor = (this.cursor + 1) % MAX_PARTICLES;
      this.positions[i * 3] = origin.x;
      this.positions[i * 3 + 1] = origin.y;
      this.positions[i * 3 + 2] = origin.z;
      const spread = 0.12;
      this.velocities[i * 3] = (dir.x + (Math.random() - 0.5) * spread) * speed;
      this.velocities[i * 3 + 1] = (dir.y + (Math.random() - 0.5) * spread) * speed;
      this.velocities[i * 3 + 2] = (dir.z + (Math.random() - 0.5) * spread) * speed;
      this.colors[i * 3] = color.r;
      this.colors[i * 3 + 1] = color.g;
      this.colors[i * 3 + 2] = color.b;
      const life = 0.12 + Math.random() * 0.1;
      this.lives[i] = life;
      this.maxLives[i] = life;
      this.lifeNorm[i] = 1;
    }
  }

  update(dt: number): void {
    for (let i = 0; i < MAX_PARTICLES; i++) {
      if (this.lives[i]! <= 0) continue;
      this.lives[i]! -= dt;
      if (this.lives[i]! < 0) this.lives[i] = 0;
      this.lifeNorm[i] = this.lives[i]! / this.maxLives[i]!;
      this.positions[i * 3]! += this.velocities[i * 3]! * dt;
      this.positions[i * 3 + 1]! += this.velocities[i * 3 + 1]! * dt;
      this.positions[i * 3 + 2]! += this.velocities[i * 3 + 2]! * dt;
    }
    (this.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (this.geometry.getAttribute('aColor') as THREE.BufferAttribute).needsUpdate = true;
    (this.geometry.getAttribute('aLife') as THREE.BufferAttribute).needsUpdate = true;
  }

  dispose(): void {
    this.geometry.dispose();
    (this.points.material as THREE.Material).dispose();
  }
}
